import React, { useState, useEffect } from 'react';
import { Modal, Form, notification } from 'antd';
import { motion } from 'framer-motion';
import { X, Search, Share2, Users, Megaphone, HelpCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { updateUser } from '@/api/user';
import posthog from 'posthog-js';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';

interface SourceSelectorModalProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: () => void;
}

const options = [
	{
		label: 'Google Search',
		description: 'You found us while searching for virtual receptionist or AI solutions',
		Icon: Search,
		bgColor: 'bg-blue-50',
	},
	{
		label: 'Social Media',
		description: 'You discovered us through LinkedIn, Facebook, or other social platforms',
		Icon: Share2,
		bgColor: 'bg-pink-50',
	},
	{
		label: 'A friend',
		description: 'A friend or colleague recommended our AI receptionist to you',
		Icon: Users,
		bgColor: 'bg-green-50',
	},
	{
		label: 'Advertisement',
		description: 'You saw our ad while browsing online or on social media',
		Icon: Megaphone,
		bgColor: 'bg-yellow-50',
	},
	{
		label: 'Other Source',
		description: 'You found us through another channel not listed above',
		Icon: HelpCircle,
		bgColor: 'bg-gray-50',
	},
];

const SourceSelectorModal: React.FC<SourceSelectorModalProps> = ({
	isOpen,
	onClose,
	onComplete,
}) => {
	const [form] = Form.useForm();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedSource, setSelectedSource] = useState<string>('');
	const [otherSourceText, setOtherSourceText] = useState<string>('');
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	// Reset form when modal opens/closes
	useEffect(() => {
		if (!isOpen) {
			setSelectedSource('');
			setOtherSourceText('');
			form.resetFields();
		}
	}, [isOpen, form]);

	const handleSourceSelect = (label: string) => {
		posthog.capture('onb_v3_source_selected', { source: label });
		setSelectedSource(label);
		if (label !== 'Other Source') {
			setOtherSourceText('');
		}
		form.setFieldsValue({ whereFoundUs: label });
		form.validateFields(['whereFoundUs']);
	};

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: (values: { whereFoundUs: string } & { agentId: string }) => {
			// If it's "Other Source" and there's additional text, append it
			const finalValue =
				values.whereFoundUs === 'Other Source' && otherSourceText
					? `Other Source: ${otherSourceText}`
					: values.whereFoundUs;

			posthog.capture('onb_v3_source_submitted', {
				source: values.whereFoundUs,
				otherSourceText: otherSourceText || undefined,
			});

			return updateUser({ whereFoundUs: finalValue, agentId: values.agentId });
		},
		onSuccess: () => {
			setIsSubmitting(false);
			notification.success({
				message: 'Thank you for your feedback!',
				duration: 3,
			});
			onComplete();
			onClose();
		},
		onError: () => {
			setIsSubmitting(false);
			posthog.capture('onb_v3_source_submission_error');
			notification.error({
				message: 'Failed to save your response',
				duration: 3,
			});
		},
	});

	const handleSubmit = (values: { whereFoundUs: string }) => {
		setIsSubmitting(true);
		mutation.mutate({ ...values, agentId: selectedAgentId as string });
	};

	return (
		<Modal
			title={
				<div className="modal-header">
					<h1 className="text-3xl font-bold mb-0">Help Us Improve</h1>
					<p className="text-gray-600">We'd love to know how you found us</p>
				</div>
			}
			open={isOpen}
			onCancel={onClose}
			footer={null}
			width={960}
			centered
			closeIcon={<X className="w-6 h-6" />}
			className="business-details-modal"
		>
			<Form form={form} className="modal-form" layout="vertical" onFinish={handleSubmit}>
				<div className="questions-container">
					<div className="space-y-6">
						<Form.Item
							name="whereFoundUs"
							rules={[{ required: true, message: 'Please select how you found us' }]}
						>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{options.map(({ label, Icon, description, bgColor }) => (
									<motion.div
										key={label}
										whileHover={{ scale: 1.02 }}
										whileTap={{ scale: 0.98 }}
										onClick={() => handleSourceSelect(label)}
										className={`
                      cursor-pointer rounded-xl p-4 border transition-all
                      hover:shadow-md hover:border-blue-200
                      ${selectedSource === label ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}
                    `}
									>
										<div className="flex flex-col space-y-3">
											<div
												className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}
											>
												<Icon
													className={`w-5 h-5 ${selectedSource === label ? 'text-blue-600' : 'text-gray-600'}`}
												/>
											</div>
											<div className="space-y-1">
												<h4 className="font-semibold text-sm text-gray-900">{label}</h4>
												<p className="text-xs text-gray-500 leading-relaxed">{description}</p>
											</div>
										</div>
									</motion.div>
								))}
							</div>
						</Form.Item>

						{/* Optional text field for Other Source */}
						{selectedSource === 'Other Source' && (
							<motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								className="mt-4"
							>
								<Form.Item label="Please specify (optional)">
									<input
										type="text"
										value={otherSourceText}
										onChange={(e) => setOtherSourceText(e.target.value)}
										placeholder="How did you hear about us?"
										className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
									/>
								</Form.Item>
							</motion.div>
						)}
					</div>
				</div>

				<div className="modal-footer">
					<button
						onClick={() => form.submit()}
						disabled={isSubmitting || !selectedSource}
						className={`w-full max-w-xs h-12 rounded-lg font-medium transition-all ${
							isSubmitting || !selectedSource
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						}`}
					>
						{isSubmitting ? 'Saving...' : 'Continue'}
					</button>
				</div>
			</Form>
		</Modal>
	);
};

export default SourceSelectorModal;
